import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, subscribePhysicalWrite } from './physicalWriteSlot';

export interface AutoSaveOperation {
  /** Same committed owner/destination lifetime; false after disable/unmount/ABA. */
  isContextCurrent: () => boolean;
  /** Also requires the dispatched edit revision and JSON draft to remain current. */
  isCurrent: () => boolean;
}
interface UseAutoSaveOptions<T> {
  delay?: number;
  onSave: (data: T, operation: AutoSaveOperation) => Promise<void>;
  onSaveStart?: () => void;
  onSaveComplete?: () => void;
  onSaveError?: (error: Error) => void;
  enabled?: boolean;
  contextKey?: string | null;
  /** Stable consumer slot, independent of owner, retained through remount. */
  writeScope?: string;
}
function fingerprint(value: unknown): string | null {
  if (value == null) return null;
  try { return JSON.stringify(value) ?? null; } catch { return null; }
}

/** Autosave JSON drafts only after explicit markAsChanged (or forceSave). */
export function useAutoSave<T>(data: T, options: UseAutoSaveOptions<T>) {
  const { delay = 3000, enabled = true, contextKey = 'default', writeScope } = options;
  const privateScope = useRef(Symbol('autosave-instance'));
  const scope = writeScope ?? privateScope.current;
  const identity = useMemo(() => ({ contextKey, scope }), [contextKey, scope]);
  const context = useMemo(() => ({ contextKey, enabled, scope, identity }), [identity, enabled]);
  const key = fingerprint(data);
  const committed = useRef<{ context: typeof context; data: T; key: string | null; options: UseAutoSaveOptions<T> } | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const edit = useRef({ identity, revision: 0, dirty: false, baseline: key, failedRevision: -1, failedKey: null as string | null });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState({ isSaving: getPhysicalWrite(scope) !== undefined, hasUnsavedChanges: false, notice: 0 });
  const notify = useCallback(() => {
    if (lifetime.current !== null) setState(previous => ({ isSaving: getPhysicalWrite(scope) !== undefined, hasUnsavedChanges: edit.current.dirty, notice: previous.notice + 1 }));
  }, [scope]);
  useLayoutEffect(() => { committed.current = { context, data, key, options }; });
  useLayoutEffect(() => {
    const epoch = Symbol('autosave-context'); lifetime.current = epoch;
    if (edit.current.identity !== identity) {
      edit.current = { identity, revision: 0, dirty: false, baseline: key, failedRevision: -1, failedKey: null };
    }
    notify();
    return () => { if (lifetime.current === epoch) lifetime.current = null; };
  }, [context, identity, notify]);
  useLayoutEffect(() => {
    if (edit.current.baseline === null && key !== null && !edit.current.dirty) edit.current.baseline = key;
  }, [context, key]);
  useLayoutEffect(() => {
    const unsubscribe = subscribePhysicalWrite(scope, notify); notify();
    return unsubscribe;
  }, [scope, notify]);

  const save = useCallback(async (force: boolean): Promise<boolean> => {
    const current = committed.current; const epoch = lifetime.current; const revision = edit.current.revision;
    if (!current || !epoch || current.context !== context || !context.enabled || context.contextKey === null
      || current.key !== key || key === null || fingerprint(current.data) !== key || getPhysicalWrite(scope) !== undefined
      || (!force && !edit.current.dirty)) return false;
    if (!force && key === edit.current.baseline) { edit.current.dirty = false; notify(); return false; }
    if (force && timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
    const snapshot = JSON.parse(key) as T;
    // A forced draft may equal the loaded baseline. Until this whole consumer
    // completes, an interrupted same-JSON revision must still be saved again.
    edit.current.baseline = null; edit.current.dirty = true;
    const operationId = Symbol('autosave-write');
    if (!acquirePhysicalWrite(scope, operationId)) return false;
    const isContextCurrent = () => lifetime.current === epoch && committed.current?.context === context
      && context.enabled && context.contextKey !== null && getPhysicalWrite(scope) === operationId;
    const isCurrent = () => isContextCurrent() && edit.current.revision === revision
      && committed.current?.key === key && fingerprint(committed.current.data) === key;
    const operation: AutoSaveOperation = { isContextCurrent, isCurrent };
    try {
      current.options.onSaveStart?.();
      await current.options.onSave(snapshot, operation);
      if (isCurrent()) {
        edit.current.baseline = key; edit.current.dirty = false;
        committed.current!.options.onSaveComplete?.();
      }
      return isCurrent();
    } catch (error) {
      if (isContextCurrent()) {
        edit.current.failedRevision = revision; edit.current.failedKey = key;
        // Failure keeps the originating draft dirty, including an explicit force.
        edit.current.dirty = true;
        if (isCurrent()) committed.current!.options.onSaveError?.(error as Error);
      }
      return false;
    } finally {
      releasePhysicalWrite(scope, operationId);
    }
  }, [context, key, scope, notify]);

  const markAsChanged = useCallback(() => {
    if (lifetime.current === null || committed.current?.context !== context || context.contextKey === null) return;
    edit.current.revision++; edit.current.dirty = true;
    edit.current.failedRevision = -1; edit.current.failedKey = null; notify();
  }, [context, notify]);
  const forceSave = useCallback(() => save(true), [save]);
  useEffect(() => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; }
    if (!context.enabled || context.contextKey === null || !state.hasUnsavedChanges || state.isSaving || key === null) return;
    if (edit.current.failedRevision === edit.current.revision && edit.current.failedKey === key) return;
    if (key === edit.current.baseline) { edit.current.dirty = false; notify(); return; }
    timer.current = setTimeout(() => { timer.current = null; void save(false); }, delay);
    return () => { if (timer.current !== null) { clearTimeout(timer.current); timer.current = null; } };
  }, [context, key, delay, save, notify, state.hasUnsavedChanges, state.isSaving, state.notice]);
  return { forceSave, markAsChanged, isSaving: state.isSaving, hasUnsavedChanges: state.hasUnsavedChanges };
}
